import { Injectable } from '@angular/core';
import { ObjectId } from 'bson';
import momentRange from 'moment-range';
import moment from 'moment-timezone';

import { Field, FieldKinds, Form, TableRow } from '@site-mate/dashpivot-shared-library';

import { ChartSettings } from 'app/shared/model/charts/chart-settings.model';
import { ChartStaticBreakdownOptions } from 'app/shared/model/charts/chart-static-breakdown-options';
import { ChartTypes } from 'app/shared/model/charts/chart-types.enum';

import { DateRangeService } from './date-range.service';
import { ChartDataIntervals } from '../model/charts/chart-data-intervals.enum';

// The require()-based block is for browsers, the import-based is for tests execution
const { extendMoment } = typeof require === 'function' ? require('moment-range') : momentRange;

const { range } = extendMoment(moment as any);

@Injectable()
export class ChartUtilsService {
  constructor(private readonly dateRangeService: DateRangeService) {}

  getChartName(chart: ChartSettings) {
    if (chart.name) {
      return chart.name;
    }

    if (!chart.template) {
      return '';
    }

    const nameGenerators = {
      [ChartTypes.Quantity]: () => this.getQuantityChartName(chart),
      [ChartTypes.Frequency]: () => this.getFrequencyChartName(chart),
      [ChartTypes.Ratio]: () => this.getRatioChartName(chart),
    };

    return nameGenerators[chart.kind]();
  }

  private getFrequencyChartName(chart: ChartSettings) {
    const { template } = chart;
    return `Number of ${template.name} forms`;
  }

  private getRatioChartName(chart: ChartSettings) {
    const { template } = chart;
    return `Breakdown of ${template.name} forms`;
  }

  private getQuantityChartName(chart: ChartSettings) {
    const { template } = chart;
    const name = [];
    const table = template.items.find((templateItem) => templateItem._id === chart.itemId);

    if (table && table.description) {
      name.push(table.description);

      if (table.kind === FieldKinds.Table) {
        const column = table.columns.find((itemColumn) => itemColumn._id === chart.columnId);
        if (column && column.heading) {
          name.push(column.heading);
        }
      }

      if (table.kind === FieldKinds.PrefilledTable) {
        table.rows.find((row, rowIndex) =>
          row.columns.find((rowColumn, columnIndex) => {
            if (rowColumn._id === chart.columnId) {
              name.push(`${table.columns[columnIndex].heading} (row ${rowIndex + 1})`);
              return true;
            }
            return false;
          }),
        );
      }
    }

    return name.join(' - ');
  }

  isChartSetup(chart: ChartSettings): boolean {
    const chartValidations = {
      [ChartTypes.Quantity]: !!(chart.templateId && chart.itemId && chart.columnId),
      [ChartTypes.Frequency]: !!chart.templateId,
      [ChartTypes.Ratio]: !!chart.templateId && !!chart.breakdownBy,
    };

    return chartValidations[chart.kind];
  }

  getRange(chart: ChartSettings, forms: Form[]) {
    if (!['all', 'custom'].includes(chart.timeframe)) {
      return this.dateRangeService.getRanges().find((dateRange) => dateRange.value === chart.timeframe).range;
    }

    if (!forms?.length) {
      return range(new Date(), new Date());
    }

    const firstFormDate = this.getFormDate(chart, forms[0]);
    const lastFormDate = this.getFormDate(chart, forms[forms.length - 1]);
    return range(new Date(lastFormDate), new Date(firstFormDate));
  }

  getFormDate(chart: ChartSettings, form: Form) {
    const sortFieldId = this.getTemplateSortFieldId(chart.template);
    const sortField = sortFieldId && form.items.find((item) => item._id === sortFieldId);

    const date = sortField && sortField.startDate ? sortField.startDate : form.createdAt;
    return moment(date).startOf(chart.dateUnit).toISOString();
  }

  getTemplateSortFieldId(template) {
    const sortFieldId = template.sortFormsBy ? template.sortFormsBy[0] : null;
    const existSortFieldInItems = sortFieldId && template.items.find(({ _id }) => _id === sortFieldId);

    return existSortFieldInItems && sortFieldId;
  }

  getURLParams(chart: ChartSettings) {
    const params = { minimalMetadata: true } as any;

    const filters = chart.filters || {};

    const timeFrame = this.dateRangeService.getRanges().find((tf) => tf.value === chart.timeframe);
    const dateRange = timeFrame?.range || range();

    filters.sortField = {
      startDate: +dateRange.start,
      endDate: +dateRange.end,
    } as any;

    filters.itemsProjection = this.getItemsProjectionFilters(chart);

    Object.keys(filters)
      .filter((key) => !!filters[key])
      .filter((key) => (Array.isArray(filters[key]) ? Boolean(filters[key].length) : true))
      .forEach((key) => {
        params[key] = JSON.stringify(filters[key]);
      });

    return params;
  }

  private getItemsProjectionFilters(chart) {
    const breakdownFields = chart.breakdownBy && chart.breakdownBy?.split('.');

    return [
      chart.template?.sortFormsBy?.filter(Boolean)?.filter(ObjectId.isValid)[0],
      chart.itemId,
      ObjectId.isValid(breakdownFields?.[0]) && breakdownFields?.[0],
    ].filter(Boolean);
  }

  getGroupNameForRow(form: Form, chart: ChartSettings, table: Field, row: TableRow) {
    const { breakdownBy } = chart;

    if (!breakdownBy || (breakdownBy as ChartStaticBreakdownOptions) === ChartStaticBreakdownOptions.None) {
      return '';
    }

    if ((breakdownBy as ChartStaticBreakdownOptions) === ChartStaticBreakdownOptions.Total) {
      return 'Total';
    }

    if ((breakdownBy as ChartStaticBreakdownOptions) === ChartStaticBreakdownOptions.Project) {
      return this.getGroupNameForProject(form);
    }

    if ((breakdownBy as ChartStaticBreakdownOptions) === ChartStaticBreakdownOptions.Team) {
      return this.getGroupNameForTeam(form);
    }

    if ((breakdownBy as ChartStaticBreakdownOptions) === ChartStaticBreakdownOptions.CreatedBy) {
      return this.getGroupNameForCreatedBy(form);
    }

    if ((breakdownBy as ChartStaticBreakdownOptions) === ChartStaticBreakdownOptions.WorkflowColumns) {
      return this.getGroupNameForWorkflowColumn(form, chart.template);
    }

    const [fieldId, columnId] = breakdownBy.split('.');

    if (fieldId && !columnId) {
      return this.getGroupNameForCategory(form, breakdownBy);
    }

    if (fieldId && columnId) {
      if (table.kind === FieldKinds.Table) {
        return this.getGroupNameForTableRow(columnId, row);
      }
      if (table.kind === FieldKinds.PrefilledTable) {
        return this.getGroupNameForPrefilledTableCell(columnId, table);
      }
    }

    return '';
  }

  private getGroupNameForProject(form) {
    return form.template.project.name;
  }

  private getGroupNameForTeam(form) {
    return form.template.team.name;
  }

  private getGroupNameForCreatedBy(form) {
    return form.createdBy.fullName;
  }

  private getGroupNameForWorkflowColumn(form, orgTemplate) {
    const column =
      form.isInColumn - 1 < orgTemplate.columns.length
        ? orgTemplate.columns[form.isInColumn - 1]
        : orgTemplate.columns[orgTemplate.columns.length - 1];

    return column.title;
  }

  // TODO: this function knows too much about the field structure,
  // this logic should probably be moved to form-fields module
  private getGroupNameForCategory(form, itemId) {
    const categoryField = form.items.find((formItem) => formItem._id === itemId);
    if (!categoryField || !categoryField?.selectedItems.length) {
      return 'No selection';
    }
    if (categoryField.selectedItems.length === 1) {
      return categoryField.selectedItems[0].value;
    }

    return 'Multiple';
  }

  private getGroupNameForTableRow(columnId, row) {
    const column = row.columns.find((rowColumn) => rowColumn.headerColumnId === columnId);
    const value = column ? column.value : '';
    return value || 'No selection';
  }

  private getGroupNameForPrefilledTableCell(columnId, table) {
    let value;
    table.rows.find((row) =>
      row.columns.find((rowColumn) => {
        if (rowColumn._id === columnId) {
          value = rowColumn.value || '';
          return true;
        }
        return false;
      }),
    );

    return value || 'No selection';
  }

  extractFieldValue(row: TableRow, chart: ChartSettings) {
    const column = row.columns.find((rowColumn) => rowColumn.headerColumnId === chart.columnId);

    if (!column) {
      return 0;
    }

    const value = Number(column.value);
    return Number.isNaN(value) ? 0 : value;
  }

  populateDates(dateUnit: ChartDataIntervals, dateRange: momentRange.DateRange) {
    const result = {};

    const adjustedRange = range(dateRange.start.startOf(dateUnit), dateRange.end.endOf(dateUnit));
    const rangeIterator = adjustedRange.by(dateUnit);

    // eslint-disable-next-line no-restricted-syntax
    for (const date of rangeIterator) {
      const dateString = date.startOf(dateUnit).toISOString();
      result[dateString] = { name: dateString, series: [], value: 0 };
    }

    return result;
  }

  isQuantityChart(chart: ChartSettings): boolean {
    return ChartTypes.Quantity === chart.kind;
  }

  isFrequencyChart(chart: ChartSettings): boolean {
    return ChartTypes.Frequency === chart.kind;
  }

  isRatioChart(chart: ChartSettings): boolean {
    return ChartTypes.Ratio === chart.kind;
  }
}
